import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from '../lib/api';
import type { StrategyBacktestResponse, UserSymphony, BacktestRunHistoryItem, BenchmarkPoint, RegimeMultiplierPolicy, BacktestRegimeComparison, BacktestMetrics, DynamicSelectExplanationEntry, VerificationSuiteResult, WalkForwardResult, WalkForwardPeriod } from '../types/api';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Line, LineChart } from 'recharts';
import { RefreshCw, TrendingUp, TrendingDown, AlertCircle, Play, DollarSign, Calendar, BarChart3, Target, Activity, Database, Clock, Percent, ListFilter, History, SlidersHorizontal, ChevronDown, ChevronUp, Layers, Download, ShieldCheck } from 'lucide-react';
import { CollapsibleCard } from '../components/CollapsibleCard';

function pearsonCorr(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 2) return null;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0, dx2 = 0, dy2 = 0;
  for (let i = 0; i < n; i++) {
    const ex = xs[i] - mx, ey = ys[i] - my;
    num += ex * ey; dx2 += ex * ex; dy2 += ey * ey;
  }
  const denom = Math.sqrt(dx2 * dy2);
  return denom > 0 ? num / denom : null;
}

function dailyRetsFromEquity(pts: BenchmarkPoint[]): Map<string, number> {
  const m = new Map<string, number>();
  for (let i = 1; i < pts.length; i++) {
    if (pts[i - 1].equity > 0)
      m.set(pts[i].date, (pts[i].equity - pts[i - 1].equity) / pts[i - 1].equity);
  }
  return m;
}

function alignedPair(
  a: { date: string; ret: number }[],
  bMap: Map<string, number>
): [number[], number[]] {
  const xs: number[] = [], ys: number[] = [];
  for (const ap of a) {
    const bv = bMap.get(ap.date);
    if (bv !== undefined) { xs.push(ap.ret); ys.push(bv); }
  }
  return [xs, ys];
}

function corrColor(r: number): string {
  const abs = Math.abs(r);
  if (abs >= 0.8) return 'text-red-600 bg-red-50';
  if (abs >= 0.5) return 'text-amber-600 bg-amber-50';
  if (abs >= 0.25) return 'text-emerald-600 bg-emerald-50';
  return 'text-emerald-700 bg-emerald-50';
}

export function Backtest() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().split('T')[0];
  });
  const getYesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  };
  const [endDate, setEndDate] = useState(() => getYesterday());
  const [startingCapital, setStartingCapital] = useState('100000');
  const [selectedSymphony, setSelectedSymphony] = useState('combined');
  const [symphonies, setSymphonies] = useState<UserSymphony[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<StrategyBacktestResponse | null>(null);
  const [showPctReturn, setShowPctReturn] = useState(false);
  const [recentRuns, setRecentRuns] = useState<BacktestRunHistoryItem[]>([]);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [selectedBenchmark, setSelectedBenchmark] = useState<string>('SPY');
  const [activeBenchmarkPoints, setActiveBenchmarkPoints] = useState<BenchmarkPoint[] | null>(null);
  const [benchmarkLoading, setBenchmarkLoading] = useState(false);
  const [customBenchmarkInput, setCustomBenchmarkInput] = useState('');
  const [compareStrategyId, setCompareStrategyId] = useState<string>('');
  const [comparePoints, setComparePoints] = useState<BenchmarkPoint[] | null>(null);
  const [compareName, setCompareName] = useState<string>('');
  const [compareLoading, setCompareLoading] = useState(false);

  // Regime multiplier state
  const [regimePolicies, setRegimePolicies] = useState<RegimeMultiplierPolicy[]>([]);
  const [applyRegimeMultiplier, setApplyRegimeMultiplier] = useState(false);
  const [regimePolicyId, setRegimePolicyId] = useState('');
  const [compareToBaseline, setCompareToBaseline] = useState(false);
  const [showRegimeSection, setShowRegimeSection] = useState(false);

  // Dynamic-select explanation state
  const [dsExplanation, setDsExplanation] = useState<DynamicSelectExplanationEntry[] | null>(null);
  const [dsRunId, setDsRunId] = useState<number | null>(null);
  const [dsLoading, setDsLoading] = useState(false);
  const [dsError, setDsError] = useState<string | null>(null);
  const [activeResultTab, setActiveResultTab] = useState<'results' | 'verification' | 'robustness'>('results');

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingRunIdRef = useRef<number | null>(null);

  const fetchHistory = useCallback(() => {
    api.getBacktestHistory().then((res) => setRecentRuns(res.runs)).catch(() => {});
  }, []);

  useEffect(() => {
    api.getSymphonies().then((res) => {
      setSymphonies(res.symphonies.filter((s) => s.is_active));
    }).catch(() => {});
    fetchHistory();
    api.getRegimeMultiplierPolicies()
      .then((res) => setRegimePolicies(res.policies ?? []))
      .catch(() => {});
  }, [fetchHistory]);

  useEffect(() => {
    if (!loading) {
      setElapsedSeconds(0);
      return;
    }
    const startTime = Date.now();
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [loading]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback((runId: number) => {
    stopPolling();
    pollIntervalRef.current = setInterval(async () => {
      if (pendingRunIdRef.current !== runId) {
        stopPolling();
        return;
      }
      try {
        const statusData = await api.getBacktestStatus(runId);
        if (statusData.status === 'complete' && statusData.result) {
          stopPolling();
          pendingRunIdRef.current = null;
          setResult(statusData.result);
          if (statusData.result.error) setError(statusData.result.error);
          setLoading(false);
          fetchHistory();
          if (statusData.result.has_dynamic_select_explanation) {
            loadDsExplanation(runId);
          }
        } else if (statusData.status === 'error') {
          stopPolling();
          pendingRunIdRef.current = null;
          setError(statusData.error || 'Backtest failed.');
          setLoading(false);
          fetchHistory();
        }
      } catch {
        // network hiccup — keep polling
      }
    }, 5000);
  }, [stopPolling, fetchHistory]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Sync benchmark and comparison state when a new result loads
  useEffect(() => {
    if (!result) {
      setActiveBenchmarkPoints(null);
      setSelectedBenchmark('SPY');
      setCustomBenchmarkInput('');
      setCompareStrategyId('');
      setComparePoints(null);
      setCompareName('');
      setDsExplanation(null);
      setDsRunId(null);
      setDsError(null);
      return;
    }
    setSelectedBenchmark('SPY');
    setActiveBenchmarkPoints(result.benchmark ?? null);
    setCompareStrategyId('');
    setComparePoints(null);
    setCompareName('');
  }, [result]);

  const loadDsExplanation = useCallback(async (runId: number) => {
    setDsLoading(true);
    setDsError(null);
    try {
      const res = await api.getDynamicSelectExplanation(runId);
      setDsExplanation(res.entries);
      setDsRunId(runId);
    } catch (err) {
      setDsError(err instanceof Error ? err.message : 'Failed to load explanation.');
    } finally {
      setDsLoading(false);
    }
  }, []);

  const handleBenchmarkChange = useCallback(async (symbol: string) => {
    if (!result) return;
    setSelectedBenchmark(symbol);
    if (symbol === 'NONE') {
      setActiveBenchmarkPoints(null);
      return;
    }
    // SPY data is already embedded in the result — no extra fetch needed
    if (symbol === 'SPY' && result.benchmark) {
      setActiveBenchmarkPoints(result.benchmark);
      return;
    }
    setBenchmarkLoading(true);
    try {
      const res = await api.getBacktestBenchmark(
        symbol,
        result.start_date,
        result.end_date,
        result.starting_capital,
      );
      setActiveBenchmarkPoints(res.points);
    } catch {
      setActiveBenchmarkPoints(null);
    } finally {
      setBenchmarkLoading(false);
    }
  }, [result]);

  const handleCompareStrategyChange = useCallback(async (strategyId: string) => {
    if (!result) return;
    setCompareStrategyId(strategyId);
    if (!strategyId) {
      setComparePoints(null);
      setCompareName('');
      return;
    }
    setCompareLoading(true);
    try {
      const res = await api.getBacktestCompare(
        strategyId,
        result.start_date,
        result.end_date,
        result.starting_capital,
      );
      setComparePoints(res.points);
      setCompareName(res.name);
    } catch {
      setComparePoints(null);
      setCompareName('');
    } finally {
      setCompareLoading(false);
    }
  }, [result]);

  const handleRunBacktest = async () => {
    stopPolling();
    pendingRunIdRef.current = null;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const symphonyId = selectedSymphony === 'combined' ? undefined : selectedSymphony;
      const submitResponse = await api.submitBacktest(
        startDate,
        endDate,
        parseFloat(startingCapital) || 100000,
        symphonyId,
        applyRegimeMultiplier ? {
          apply_regime_multiplier: true,
          regime_multiplier_policy_id: regimePolicyId || null,
          compare_to_baseline: compareToBaseline,
        } : undefined
      );

      // Cache hit — result returned synchronously, no polling needed
      if (submitResponse.status === 'complete' && submitResponse.result) {
        setResult(submitResponse.result);
        if (submitResponse.result.error) setError(submitResponse.result.error);
        setLoading(false);
        fetchHistory();
        if (submitResponse.result.has_dynamic_select_explanation && submitResponse.run_id) {
          loadDsExplanation(submitResponse.run_id);
        }
        return;
      }

      // Cache miss — background task running, start polling
      pendingRunIdRef.current = submitResponse.run_id;
      startPolling(submitResponse.run_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit backtest.');
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const formatPct = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  };

  const formatRunAt = (isoStr: string) => {
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const benchmarkMetrics = (() => {
    if (!activeBenchmarkPoints || activeBenchmarkPoints.length < 2) return null;
    const bench = activeBenchmarkPoints;
    const startEq = bench[0].equity;
    const endEq = bench[bench.length - 1].equity;
    const startD = new Date(bench[0].date + 'T00:00:00');
    const endD = new Date(bench[bench.length - 1].date + 'T00:00:00');
    const years = (endD.getTime() - startD.getTime()) / (365.25 * 24 * 3600 * 1000);
    const totalRetPct = ((endEq - startEq) / startEq) * 100;
    const annRetPct = years > 0 ? ((Math.pow(endEq / startEq, 1 / years) - 1) * 100) : 0;
    let peak = startEq;
    let maxDD = 0;
    const dailyReturns: number[] = [];
    for (let i = 1; i < bench.length; i++) {
      if (bench[i].equity > peak) peak = bench[i].equity;
      const dd = (peak - bench[i].equity) / peak * 100;
      if (dd > maxDD) maxDD = dd;
      dailyReturns.push((bench[i].equity - bench[i - 1].equity) / bench[i - 1].equity);
    }
    const meanDR = dailyReturns.reduce((a, b) => a + b, 0) / dailyReturns.length;
    const stdDR = Math.sqrt(dailyReturns.reduce((a, b) => a + (b - meanDR) ** 2, 0) / dailyReturns.length);
    const sharpe = stdDR > 0 ? (meanDR / stdDR) * Math.sqrt(252) : 0;
    const calmar = maxDD > 0 ? annRetPct / maxDD : null;
    return { totalRetPct, annRetPct, maxDD, sharpe, calmar, startDate: bench[0].date, endDate: bench[bench.length - 1].date };
  })();

  const compareMetrics = (() => {
    if (!comparePoints || comparePoints.length < 2) return null;
    const pts = comparePoints;
    const startEq = pts[0].equity;
    const endEq = pts[pts.length - 1].equity;
    const startD = new Date(pts[0].date + 'T00:00:00');
    const endD = new Date(pts[pts.length - 1].date + 'T00:00:00');
    const years = (endD.getTime() - startD.getTime()) / (365.25 * 24 * 3600 * 1000);
    const totalRetPct = ((endEq - startEq) / startEq) * 100;
    const annRetPct = years > 0 ? ((Math.pow(endEq / startEq, 1 / years) - 1) * 100) : 0;
    let peak = startEq;
    let maxDD = 0;
    const dailyReturns: number[] = [];
    for (let i = 1; i < pts.length; i++) {
      if (pts[i].equity > peak) peak = pts[i].equity;
      const dd = (peak - pts[i].equity) / peak * 100;
      if (dd > maxDD) maxDD = dd;
      dailyReturns.push((pts[i].equity - pts[i - 1].equity) / pts[i - 1].equity);
    }
    const meanDR = dailyReturns.reduce((a, b) => a + b, 0) / dailyReturns.length;
    const stdDR = Math.sqrt(dailyReturns.reduce((a, b) => a + (b - meanDR) ** 2, 0) / dailyReturns.length);
    const sharpe = stdDR > 0 ? (meanDR / stdDR) * Math.sqrt(252) : 0;
    const calmar = maxDD > 0 ? annRetPct / maxDD : null;
    return { totalRetPct, annRetPct, maxDD, sharpe, calmar, startDate: pts[0].date, endDate: pts[pts.length - 1].date };
  })();

  const correlationStats = (() => {
    if (!result?.equity_curve || result.equity_curve.length < 2) return null;
    const hasCompare = comparePoints && comparePoints.length >= 2;
    const hasBench = activeBenchmarkPoints && activeBenchmarkPoints.length >= 2 && selectedBenchmark !== 'NONE';
    if (!hasCompare && !hasBench) return null;

    const primaryRets = result.equity_curve
      .filter(pt => pt.daily_return_pct != null)
      .map(pt => ({ date: pt.date, ret: pt.daily_return_pct / 100 }));

    const benchMap = hasBench ? dailyRetsFromEquity(activeBenchmarkPoints!) : null;
    const compMap = hasCompare ? dailyRetsFromEquity(comparePoints!) : null;

    let primaryVsCompare: number | null = null;
    let primaryVsBenchmark: number | null = null;
    let compareVsBenchmark: number | null = null;
    let nPvC = 0, nPvB = 0, nCvB = 0;

    if (compMap) {
      const [xs, ys] = alignedPair(primaryRets, compMap);
      primaryVsCompare = pearsonCorr(xs, ys); nPvC = xs.length;
    }
    if (benchMap) {
      const [xs, ys] = alignedPair(primaryRets, benchMap);
      primaryVsBenchmark = pearsonCorr(xs, ys); nPvB = xs.length;
    }
    if (compMap && benchMap) {
      const compRets = comparePoints!.slice(1).map((pt, i) => ({
        date: pt.date,
        ret: comparePoints![i].equity > 0 ? (pt.equity - comparePoints![i].equity) / comparePoints![i].equity : 0,
      }));
      const [xs, ys] = alignedPair(compRets, benchMap);
      compareVsBenchmark = pearsonCorr(xs, ys); nCvB = xs.length;
    }

    if (primaryVsCompare === null && primaryVsBenchmark === null) return null;
    return { primaryVsCompare, primaryVsBenchmark, compareVsBenchmark, nPvC, nPvB, nCvB };
  })();

  const chartData = result?.equity_curve.map((pt) => {
    const benchmarkPt = activeBenchmarkPoints?.find((b) => b.date === pt.date);
    const comparePt = comparePoints?.find((c) => c.date === pt.date);
    return {
      date: pt.date,
      equity: pt.equity,
      cash: pt.cash,
      positions: pt.positions_value,
      returnPct: result.starting_capital > 0
        ? ((pt.equity / result.starting_capital) - 1) * 100
        : 0,
      benchEquity: benchmarkPt?.equity,
      benchReturnPct: benchmarkPt?.return_pct,
      compareEquity: comparePt?.equity,
      compareReturnPct: comparePt?.return_pct,
    };
  });

  // Compute the date span to decide whether x-axis ticks show day or year.
  // Short ranges (≤180 days) show "May 14"; longer ranges show "May '24".
  const chartDateSpanDays = (() => {
    if (!chartData || chartData.length < 2) return 0;
    const a = new Date(chartData[0].date + 'T00:00:00').getTime();
    const b = new Date(chartData[chartData.length - 1].date + 'T00:00:00').getTime();
    return (b - a) / 86_400_000;
  })();

  const handleExportCSV = useCallback(() => {
    if (!result) return;

    const fmt = (n: number, decimals = 4) => n.toFixed(decimals);
    const esc = (v: string | number | undefined | null) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const row = (...cols: (string | number | undefined | null)[]) =>
      cols.map(esc).join(',');

    const lines: string[] = [];

    // ── Summary ──────────────────────────────────────────────────────────
    lines.push('Backtest Summary');
    lines.push(row('Start Date', result.start_date));
    lines.push(row('End Date', result.end_date));
    lines.push(row('Starting Capital', fmt(result.starting_capital, 2)));
    lines.push(row('Ending Equity', fmt(result.ending_equity, 2)));
    lines.push(row('Total Return (%)', fmt(result.metrics.total_return_pct, 4)));
    lines.push(row('Annualized Return (%)', fmt(result.metrics.annualized_return_pct, 4)));
    lines.push(row('Max Drawdown (%)', fmt(result.metrics.max_drawdown_pct, 4)));
    lines.push(row('Max Drawdown Date', result.metrics.max_drawdown_date ?? ''));
    lines.push(row('Sharpe Ratio', fmt(result.metrics.sharpe_ratio, 4)));
    lines.push(row('Volatility (%)', fmt(result.metrics.volatility_pct, 4)));
    lines.push(row('Win Rate (%)', fmt(result.metrics.win_rate_pct, 4)));
    lines.push(row('Best Day (%)', fmt(result.metrics.best_day_pct, 4)));
    lines.push(row('Worst Day (%)', fmt(result.metrics.worst_day_pct, 4)));
    lines.push(row('Total Trading Days', result.metrics.total_trading_days));
    lines.push(row('Price Source', result.price_source));

    // ── Equity Curve ─────────────────────────────────────────────────────
    lines.push('');
    lines.push('Equity Curve');
    lines.push(row('Date', 'Equity ($)', 'Cash ($)', 'Positions Value ($)', 'Daily Return (%)'));
    for (const pt of result.equity_curve) {
      lines.push(row(
        pt.date,
        fmt(pt.equity, 2),
        fmt(pt.cash, 2),
        fmt(pt.positions_value, 2),
        fmt(pt.daily_return_pct, 6),
      ));
    }

    // ── Strategy Contributions ────────────────────────────────────────────
    if (result.strategy_contributions.length > 0) {
      lines.push('');
      lines.push('Strategy Contributions');
      lines.push(row('Strategy', 'Allocation (%)', 'Total Return (%)', 'Annualized Return (%)'));
      for (const sc of result.strategy_contributions) {
        lines.push(row(
          sc.name,
          fmt(sc.allocation_percent, 2),
          fmt(sc.total_return_pct, 4),
          fmt(sc.annualized_return_pct, 4),
        ));
      }
    }

    // ── Daily Trade Log ───────────────────────────────────────────────────
    if (result.daily_trade_log && result.daily_trade_log.length > 0) {
      // Section 1: Per-day portfolio P&L with per-symbol breakdown
      lines.push('');
      lines.push('Daily P&L by Symbol');
      lines.push('Notes: Per-symbol P&L = price change × shares held at start of day (before rebalance). Portfolio P&L = total equity change for the day.');
      lines.push('');

      // Collect all symbols that appear across all days for a consistent column order
      const allSymbols = Array.from(
        new Set(result.daily_trade_log.flatMap(d => Object.keys(d.per_symbol_pnl)))
      ).sort();

      if (allSymbols.length > 0) {
        lines.push(row('Date', 'Portfolio P&L ($)', ...allSymbols.map(s => `${s} P&L ($)`)));
        for (const d of result.daily_trade_log) {
          lines.push(row(
            d.date,
            fmt(d.portfolio_pnl_usd, 2),
            ...allSymbols.map(s => {
              const v = d.per_symbol_pnl[s];
              return v !== undefined ? fmt(v, 2) : '0.00';
            }),
          ));
        }
      }

      // Section 2: Individual rebalance trades
      const allTrades = result.daily_trade_log.flatMap(d =>
        d.trades.map(t => ({ date: d.date, ...t }))
      );

      if (allTrades.length > 0) {
        lines.push('');
        lines.push('Rebalance Trades');
        lines.push('Notes: Trades executed at market close price. Sells occur before buys within each day. Trade Value = Shares × Price.');
        lines.push('');
        lines.push(row('Date', 'Symbol', 'Action', 'Shares', 'Price ($)', 'Trade Value ($)'));
        for (const t of allTrades) {
          lines.push(row(
            t.date,
            t.symbol,
            t.action,
            t.shares,
            fmt(t.price, 4),
            fmt(t.trade_value, 2),
          ));
        }
      } else {
        lines.push('');
        lines.push('Rebalance Trades');
        lines.push('No rebalance trades executed (portfolio held constant or no weight changes).');
      }
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `backtest_${result.start_date}_to_${result.end_date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [result]);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Strategy Backtest</h1>
        <p className="text-gray-600 mt-1 text-base">
          Test your allocated strategies against historical data
        </p>
      </div>

      <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-600" />
          Backtest Parameters
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2 lg:col-span-4">
            <label htmlFor="strategy" className="block text-sm font-semibold text-gray-700 mb-1">
              <span className="flex items-center gap-1.5">
                <ListFilter className="w-4 h-4 text-emerald-600" />
                Strategy
              </span>
            </label>
            <select
              id="strategy"
              value={selectedSymphony}
              onChange={(e) => setSelectedSymphony(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
              disabled={loading}
            >
              <option value="combined">Combined Strategy (All Allocated)</option>
              {symphonies.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name || s.symphony_id} — {s.source_mode.toUpperCase()}{s.allocation_percent > 0 ? ` (${s.allocation_percent}%)` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="start-date" className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="end-date" className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              max={getYesterday()}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              disabled={loading}
            />
          </div>
          <div>
            <label htmlFor="capital" className="block text-sm font-semibold text-gray-700 mb-1">Starting Capital ($)</label>
            <input
              id="capital"
              type="number"
              value={startingCapital}
              onChange={(e) => setStartingCapital(e.target.value)}
              min="1000"
              step="1000"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              disabled={loading}
            />
          </div>
        </div>
        {/* Regime Multiplier Section */}
        <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowRegimeSection((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-semibold text-gray-700"
          >
            <span className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-emerald-600" />
              Regime Position Multiplier
              {applyRegimeMultiplier && (
                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-700 uppercase">ON</span>
              )}
            </span>
            {showRegimeSection ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {showRegimeSection && (
            <div className="px-4 py-4 space-y-4">
              <p className="text-xs text-gray-500">
                Scales each asset's weight based on its detected market regime before running the backtest simulation.
              </p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyRegimeMultiplier}
                    onChange={(e) => setApplyRegimeMultiplier(e.target.checked)}
                    disabled={loading}
                    className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">Enable regime multiplier</span>
                </label>
              </div>
              {applyRegimeMultiplier && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Policy</label>
                    <select
                      value={regimePolicyId}
                      onChange={(e) => setRegimePolicyId(e.target.value)}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 bg-white disabled:opacity-60"
                    >
                      <option value="">— Use default policy —</option>
                      {regimePolicies.map((p) => (
                        <option key={p.policy_id} value={p.policy_id}>
                          {p.name} ({p.policy_id}){p.is_default ? ' [default]' : ''}
                        </option>
                      ))}
                    </select>
                    {regimePolicyId && (() => {
                      const pol = regimePolicies.find((p) => p.policy_id === regimePolicyId);
                      if (!pol) return null;
                      return (
                        <p className="mt-1 text-xs text-gray-500">
                          Bullish ×{pol.bullish_factor} · Neutral ×{pol.neutral_factor} · Bearish ×{pol.bearish_factor}
                          {pol.normalize_weights ? ' · Normalize on' : ` · Cash fallback → ${pol.cash_fallback_symbol}`}
                        </p>
                      );
                    })()}
                  </div>
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={compareToBaseline}
                      onChange={(e) => setCompareToBaseline(e.target.checked)}
                      disabled={loading}
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div>
                      <span className="text-sm font-semibold text-gray-700">Compare to baseline</span>
                      <p className="text-xs text-gray-400">Runs both with and without multiplier so you can compare performance side-by-side.</p>
                    </div>
                  </label>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-4">
          <button
            onClick={handleRunBacktest}
            disabled={loading || !startDate || !endDate}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold rounded-lg bg-emerald-700 text-white shadow-md hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Running Backtest... ({elapsedSeconds}s)
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run Backtest
              </>
            )}
          </button>
          {loading && (
            <p className="text-sm text-gray-500 mt-2">
              {elapsedSeconds < 15
                ? 'Job submitted — fetching historical data...'
                : elapsedSeconds < 60
                ? 'Running simulation — this takes about a minute for large strategies...'
                : 'Still running — large strategies with long date ranges can take several minutes. Results will appear automatically when ready.'}
            </p>
          )}
        </div>
      </div>

      {recentRuns.length > 0 && (
        <CollapsibleCard title="Recent Runs" icon={<History className="w-5 h-5 text-emerald-600" />}>
          <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Run</th>
                  <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Strategy</th>
                  <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Period</th>
                  <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Total Ret.</th>
                  <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Ann. Ret.</th>
                  <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Max DD</th>
                  <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Sharpe</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Calmar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {recentRuns.map((run, idx) => {
                  const totalRet = run.total_return_pct ?? 0;
                  const annRet = run.annualized_return_pct ?? null;
                  const calmar = (annRet !== null && run.max_drawdown_pct != null && run.max_drawdown_pct > 0)
                    ? annRet / run.max_drawdown_pct
                    : null;
                  return (
                    <tr key={run.id} className={idx === 0 ? 'bg-emerald-50' : 'hover:bg-gray-50'}>
                      <td className="py-2.5 pr-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {idx === 0 && (
                            <span className="text-xs font-semibold bg-emerald-600 text-white px-1.5 py-0.5 rounded">Latest</span>
                          )}
                          <span className="text-xs text-gray-500">{formatRunAt(run.run_at)}</span>
                        </div>
                      </td>
                      <td className="py-2.5 pr-4 max-w-[180px]">
                        <span className="text-gray-800 font-medium truncate block" title={run.strategy_label ?? ''}>
                          {run.strategy_label ?? '—'}
                        </span>
                      </td>
                      <td className="py-2.5 pr-4 whitespace-nowrap text-gray-500 text-xs">
                        {formatDate(run.start_date)} → {formatDate(run.end_date)}
                      </td>
                      <td className={`py-2.5 pr-4 text-right whitespace-nowrap font-semibold ${totalRet >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {formatPct(totalRet)}
                      </td>
                      <td className={`py-2.5 pr-4 text-right whitespace-nowrap font-semibold ${annRet === null ? 'text-gray-400' : annRet >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {annRet !== null ? formatPct(annRet) : '—'}
                      </td>
                      <td className="py-2.5 pr-4 text-right whitespace-nowrap font-semibold text-red-600">
                        {run.max_drawdown_pct != null ? `-${run.max_drawdown_pct.toFixed(2)}%` : '—'}
                      </td>
                      <td className="py-2.5 pr-4 text-right whitespace-nowrap text-gray-700">
                        {run.sharpe_ratio != null ? run.sharpe_ratio.toFixed(2) : '—'}
                      </td>
                      <td className={`py-2.5 text-right whitespace-nowrap font-semibold ${calmar === null ? 'text-gray-400' : calmar >= 1 ? 'text-emerald-700' : 'text-amber-600'}`}>
                        {calmar !== null ? calmar.toFixed(2) : '—'}
                      </td>
                    </tr>
                  );
                })}
                {compareMetrics && recentRuns.length > 0 && (
                  <tr className="bg-amber-50 border-t-2 border-amber-200">
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      <span className="text-xs font-semibold bg-amber-500 text-white px-1.5 py-0.5 rounded">Compare</span>
                    </td>
                    <td className="py-2.5 pr-4 max-w-[180px]">
                      <span className="text-amber-800 font-medium text-xs truncate block" title={compareName}>{compareName}</span>
                    </td>
                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-500 text-xs">
                      {formatDate(compareMetrics.startDate)} → {formatDate(compareMetrics.endDate)}
                    </td>
                    <td className={`py-2.5 pr-4 text-right whitespace-nowrap font-semibold ${compareMetrics.totalRetPct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {formatPct(compareMetrics.totalRetPct)}
                    </td>
                    <td className={`py-2.5 pr-4 text-right whitespace-nowrap font-semibold ${compareMetrics.annRetPct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {formatPct(compareMetrics.annRetPct)}
                    </td>
                    <td className="py-2.5 pr-4 text-right whitespace-nowrap font-semibold text-red-600">
                      -{compareMetrics.maxDD.toFixed(2)}%
                    </td>
                    <td className="py-2.5 pr-4 text-right whitespace-nowrap text-gray-700">
                      {compareMetrics.sharpe.toFixed(2)}
                    </td>
                    <td className={`py-2.5 text-right whitespace-nowrap font-semibold ${compareMetrics.calmar === null ? 'text-gray-400' : compareMetrics.calmar >= 1 ? 'text-emerald-700' : 'text-amber-600'}`}>
                      {compareMetrics.calmar !== null ? compareMetrics.calmar.toFixed(2) : '—'}
                    </td>
                  </tr>
                )}
                {benchmarkMetrics && recentRuns.length > 0 && selectedBenchmark !== 'NONE' && (
                  <tr className="bg-indigo-50 border-t-2 border-indigo-200">
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      <span className="text-xs font-semibold bg-indigo-600 text-white px-1.5 py-0.5 rounded">{selectedBenchmark} B&H</span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className="text-indigo-700 font-medium text-xs">{selectedBenchmark} Buy &amp; Hold</span>
                    </td>
                    <td className="py-2.5 pr-4 whitespace-nowrap text-gray-500 text-xs">
                      {formatDate(benchmarkMetrics.startDate)} → {formatDate(benchmarkMetrics.endDate)}
                    </td>
                    <td className={`py-2.5 pr-4 text-right whitespace-nowrap font-semibold ${benchmarkMetrics.totalRetPct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {formatPct(benchmarkMetrics.totalRetPct)}
                    </td>
                    <td className={`py-2.5 pr-4 text-right whitespace-nowrap font-semibold ${benchmarkMetrics.annRetPct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {formatPct(benchmarkMetrics.annRetPct)}
                    </td>
                    <td className="py-2.5 pr-4 text-right whitespace-nowrap font-semibold text-red-600">
                      -{benchmarkMetrics.maxDD.toFixed(2)}%
                    </td>
                    <td className="py-2.5 pr-4 text-right whitespace-nowrap text-gray-700">
                      {benchmarkMetrics.sharpe.toFixed(2)}
                    </td>
                    <td className={`py-2.5 text-right whitespace-nowrap font-semibold ${benchmarkMetrics.calmar === null ? 'text-gray-400' : benchmarkMetrics.calmar >= 1 ? 'text-emerald-700' : 'text-amber-600'}`}>
                      {benchmarkMetrics.calmar !== null ? benchmarkMetrics.calmar.toFixed(2) : '—'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </CollapsibleCard>
      )}

      {error && !result?.equity_curve.length && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 text-base">{error}</p>
        </div>
      )}

      {result && result.equity_curve.length > 0 && (
        <>
          {/* Results / Verification Suite / Robustness Tests tab switcher */}
          <div className="flex gap-0 border-b border-gray-200 bg-white rounded-t-lg overflow-x-auto scrollbar-none">
            {([
              { key: 'results', label: 'Results', icon: null },
              { key: 'verification', label: 'Verification Suite', icon: <ShieldCheck className="w-3.5 h-3.5" /> },
              { key: 'robustness', label: 'Robustness Tests', icon: <BarChart3 className="w-3.5 h-3.5" /> },
            ] as const).map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setActiveResultTab(key)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  activeResultTab === key
                    ? 'border-violet-600 text-violet-700 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>

          {activeResultTab === 'results' && (<>
          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-amber-800 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              icon={<DollarSign className="w-5 h-5" />}
              label="Ending Equity"
              value={formatCurrency(result.ending_equity)}
              subtext={formatPct(result.metrics.total_return_pct)}
              positive={result.metrics.total_return_pct >= 0}
            />
            <MetricCard
              icon={<TrendingUp className="w-5 h-5" />}
              label="Annualized Return"
              value={formatPct(result.metrics.annualized_return_pct)}
              positive={result.metrics.annualized_return_pct >= 0}
            />
            <MetricCard
              icon={<TrendingDown className="w-5 h-5" />}
              label="Max Drawdown"
              value={`-${result.metrics.max_drawdown_pct.toFixed(2)}%`}
              subtext={result.metrics.max_drawdown_date
                ? `trough ${new Date(result.metrics.max_drawdown_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                : undefined}
              positive={false}
            />
            <MetricCard
              icon={<Activity className="w-5 h-5" />}
              label="Sharpe Ratio"
              value={result.metrics.sharpe_ratio.toFixed(2)}
              positive={result.metrics.sharpe_ratio > 0}
            />
          </div>

          {correlationStats && (
            <div className="bg-white rounded-lg px-6 py-4 shadow-md border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                <Activity className="w-4 h-4 text-violet-500" />
                Return Correlation
                <span className="ml-1 text-xs font-normal text-gray-400">(daily, Pearson)</span>
              </h3>
              <div className="flex flex-col gap-2">
                {correlationStats.primaryVsCompare !== null && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <span className="text-gray-500 min-w-0 flex-1 truncate">
                      Primary <span className="text-gray-400 mx-0.5">↔</span> {compareName || 'Compare'}
                    </span>
                    <span className={`font-mono font-bold px-2 py-0.5 rounded text-sm shrink-0 ${corrColor(correlationStats.primaryVsCompare)}`}>
                      {correlationStats.primaryVsCompare.toFixed(3)}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">n={correlationStats.nPvC}</span>
                  </div>
                )}
                {correlationStats.primaryVsBenchmark !== null && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <span className="text-gray-500 min-w-0 flex-1 truncate">
                      Primary <span className="text-gray-400 mx-0.5">↔</span> {selectedBenchmark}
                    </span>
                    <span className={`font-mono font-bold px-2 py-0.5 rounded text-sm shrink-0 ${corrColor(correlationStats.primaryVsBenchmark)}`}>
                      {correlationStats.primaryVsBenchmark.toFixed(3)}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">n={correlationStats.nPvB}</span>
                  </div>
                )}
                {correlationStats.compareVsBenchmark !== null && (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <span className="text-gray-500 min-w-0 flex-1 truncate">
                      {compareName || 'Compare'} <span className="text-gray-400 mx-0.5">↔</span> {selectedBenchmark}
                    </span>
                    <span className={`font-mono font-bold px-2 py-0.5 rounded text-sm shrink-0 ${corrColor(correlationStats.compareVsBenchmark)}`}>
                      {correlationStats.compareVsBenchmark.toFixed(3)}
                    </span>
                    <span className="text-xs text-gray-400 shrink-0">n={correlationStats.nCvB}</span>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-400">
                Green ≤ 0.5 · Amber 0.5–0.8 · Red ≥ 0.8 · Negative values indicate inverse relationship
              </p>
            </div>
          )}

          <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-600" />
                Equity Curve
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 border border-gray-300 hover:border-emerald-300 rounded-lg transition-colors"
                  title="Download backtest results as CSV"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setShowPctReturn(false)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      !showPctReturn
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <DollarSign className="w-3.5 h-3.5" />
                    Value
                  </button>
                  <button
                    onClick={() => setShowPctReturn(true)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      showPctReturn
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Percent className="w-3.5 h-3.5" />
                    Return
                  </button>
                </div>
              </div>
            </div>
            {/* Compare strategy selector */}
            <div className="flex flex-wrap items-center gap-2 mb-3 py-2.5 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0">Compare strategy:</span>
              <select
                value={compareStrategyId}
                onChange={(e) => handleCompareStrategyChange(e.target.value)}
                disabled={compareLoading}
                className="flex-1 min-w-0 max-w-xs px-3 py-1 text-xs border border-gray-300 rounded-full focus:ring-1 focus:ring-amber-400 focus:border-amber-400 bg-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">— None —</option>
                <option value="combined">Combined Strategy (All Allocated)</option>
                {symphonies.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    {s.name || s.symphony_id}{s.allocation_percent > 0 ? ` (${s.allocation_percent}%)` : ''}
                  </option>
                ))}
              </select>
              {compareLoading && (
                <span className="flex items-center gap-1 text-xs text-amber-600">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Running comparison…
                </span>
              )}
              {comparePoints && compareName && !compareLoading && (
                <span className="text-xs text-amber-700 font-medium truncate">vs {compareName}</span>
              )}
            </div>

            {/* Benchmark selector */}
            <div className="flex flex-wrap items-center gap-2 mb-3 py-2.5 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0">Benchmark:</span>
              {(['NONE', 'SPY', 'QQQ', 'IWM', 'AGG', 'GLD'] as const).map((sym) => (
                <button
                  key={sym}
                  onClick={() => handleBenchmarkChange(sym)}
                  disabled={benchmarkLoading}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${
                    selectedBenchmark === sym
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-indigo-400 hover:text-indigo-600'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  {sym === 'NONE' ? 'None' : sym}
                </button>
              ))}
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  placeholder="Custom ticker…"
                  value={customBenchmarkInput}
                  onChange={(e) => setCustomBenchmarkInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && customBenchmarkInput.trim()) {
                      handleBenchmarkChange(customBenchmarkInput.trim());
                    }
                  }}
                  className={`w-28 px-2.5 py-1 text-xs border rounded-full focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 uppercase disabled:opacity-60 ${
                    !['NONE','SPY','QQQ','IWM','AGG','GLD'].includes(selectedBenchmark) && selectedBenchmark
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-300'
                  }`}
                  disabled={benchmarkLoading}
                  maxLength={10}
                />
                <button
                  onClick={() => { if (customBenchmarkInput.trim()) handleBenchmarkChange(customBenchmarkInput.trim()); }}
                  disabled={benchmarkLoading || !customBenchmarkInput.trim()}
                  className="px-2.5 py-1 text-xs font-medium rounded-full border border-indigo-400 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {benchmarkLoading ? '…' : 'Apply'}
                </button>
              </div>
            </div>

            {/* Chart legend */}
            <div className="flex flex-wrap items-center gap-4 mb-2 text-sm text-gray-500">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-0.5 bg-emerald-600 rounded"></div>
                <span>Strategy</span>
              </div>
              {comparePoints && selectedBenchmark !== 'NONE' && (
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-0.5" style={{ borderTop: '2px solid #f59e0b' }}></div>
                  <span className="truncate max-w-[160px]" title={compareName}>{compareName}</span>
                </div>
              )}
              {activeBenchmarkPoints && selectedBenchmark !== 'NONE' && (
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-0.5" style={{ borderTop: '2px dashed #6366f1' }}></div>
                  <span>{selectedBenchmark} (Buy &amp; Hold)</span>
                  {benchmarkLoading && <RefreshCw className="w-3 h-3 animate-spin text-indigo-400" />}
                </div>
              )}
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="backtestGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(v) => {
                      const d = new Date(v + 'T00:00:00');
                      return d.toLocaleDateString('en-US', chartDateSpanDays > 180
                        ? { month: 'short', year: '2-digit' }
                        : { month: 'short', day: 'numeric' });
                    }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickFormatter={(v) => showPctReturn ? `${v.toFixed(1)}%` : `$${(v / 1000).toFixed(0)}k`}
                    width={60}
                  />
                  <Tooltip
                    formatter={((value: number | undefined, name: string | undefined) => {
                      const n = name ?? '';
                      if (value === undefined) return ['—', n];
                      const fmt$ = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
                      const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
                      if (n === 'equity') return [fmt$(value), 'Strategy'];
                      if (n === 'returnPct') return [fmtPct(value), 'Strategy Return'];
                      if (n === 'benchEquity') return [fmt$(value), `${selectedBenchmark} B&H`];
                      if (n === 'benchReturnPct') return [fmtPct(value), `${selectedBenchmark} Return`];
                      if (n === 'compareEquity') return [fmt$(value), compareName || 'Compare'];
                      if (n === 'compareReturnPct') return [fmtPct(value), `${compareName || 'Compare'} Return`];
                      return [value, n];
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    }) as any}
                    labelFormatter={(label) => {
                      const d = new Date(label + 'T00:00:00');
                      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    }}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Area
                    type="monotone"
                    dataKey={showPctReturn ? 'returnPct' : 'equity'}
                    stroke="#059669"
                    strokeWidth={2}
                    fill="url(#backtestGrad)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  {comparePoints && (
                    <Line
                      type="monotone"
                      dataKey={showPctReturn ? 'compareReturnPct' : 'compareEquity'}
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />
                  )}
                  {activeBenchmarkPoints && selectedBenchmark !== 'NONE' && (
                    <Line
                      type="monotone"
                      dataKey={showPctReturn ? 'benchReturnPct' : 'benchEquity'}
                      stroke="#6366f1"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                      dot={false}
                      activeDot={{ r: 3 }}
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              icon={<Clock className="w-5 h-5" />}
              label="Trading Days"
              value={String(result.metrics.total_trading_days)}
            />
            <MetricCard
              icon={<Target className="w-5 h-5" />}
              label="Win Rate"
              value={`${result.metrics.win_rate_pct.toFixed(1)}%`}
              positive={result.metrics.win_rate_pct >= 50}
            />
            <MetricCard
              icon={<TrendingUp className="w-5 h-5" />}
              label="Best Day"
              value={formatPct(result.metrics.best_day_pct)}
              positive={true}
            />
            <MetricCard
              icon={<TrendingDown className="w-5 h-5" />}
              label="Worst Day"
              value={formatPct(result.metrics.worst_day_pct)}
              positive={false}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <MetricCard
              icon={<Activity className="w-5 h-5" />}
              label="Volatility (Ann.)"
              value={`${result.metrics.volatility_pct.toFixed(2)}%`}
            />
            <MetricCard
              icon={<Database className="w-5 h-5" />}
              label="Price Source"
              value={result.price_source}
              subtext={result.price_time}
            />
          </div>

          {result.strategy_contributions.length > 0 && (result.strategy_contributions.length > 1 || result.strategy_contributions.some(sc => sc.pnl_usd !== undefined)) && (
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Strategy Sleeve Attribution
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Sleeve</th>
                      <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Alloc %</th>
                      <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">CAGR</th>
                      <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Total P&amp;L</th>
                      <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Portfolio Contribution</th>
                      <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Max DD</th>
                      <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Top Winner</th>
                      <th className="text-left py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Top Loser</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.strategy_contributions.map((sc) => {
                      const winner = sc.top_winners?.[0];
                      const loser = sc.top_losers?.[0];
                      return (
                        <tr key={sc.symphony_id} className="hover:bg-gray-50">
                          <td className="py-2.5 pr-3 font-medium text-gray-800 max-w-[160px] truncate" title={sc.name}>{sc.name}</td>
                          <td className="py-2.5 pr-3 text-right text-gray-500 tabular-nums">{sc.allocation_percent.toFixed(1)}%</td>
                          <td className={`py-2.5 pr-3 text-right font-semibold tabular-nums ${sc.annualized_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {formatPct(sc.annualized_return_pct)}
                          </td>
                          <td className={`py-2.5 pr-3 text-right tabular-nums ${(sc.pnl_usd ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {sc.pnl_usd !== undefined ? formatCurrency(sc.pnl_usd) : formatPct(sc.total_return_pct)}
                          </td>
                          <td className={`py-2.5 pr-3 text-right tabular-nums font-medium ${(sc.contribution_to_portfolio_pct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {sc.contribution_to_portfolio_pct !== undefined
                              ? `${sc.contribution_to_portfolio_pct >= 0 ? '+' : ''}${sc.contribution_to_portfolio_pct.toFixed(2)}%`
                              : '—'}
                          </td>
                          <td className="py-2.5 pr-3 text-right text-red-600 tabular-nums">
                            {sc.max_drawdown_pct !== undefined ? `-${sc.max_drawdown_pct.toFixed(2)}%` : '—'}
                          </td>
                          <td className="py-2.5 pr-3 text-left">
                            {winner ? (
                              <span className="text-emerald-700 font-mono text-xs">
                                {winner.symbol} <span className="text-gray-400">(+${Math.round(winner.pnl_usd).toLocaleString()})</span>
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-2.5 text-left">
                            {loser ? (
                              <span className="text-red-600 font-mono text-xs">
                                {loser.symbol} <span className="text-gray-400">(${Math.round(loser.pnl_usd).toLocaleString()})</span>
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Regime Multiplier Comparison */}
          {result.regime_comparison && (
            <RegimeComparisonPanel
              comparison={result.regime_comparison}
              startingCapital={result.starting_capital}
              formatCurrency={formatCurrency}
              formatPct={formatPct}
            />
          )}

          {/* Dynamic Selection Explanation */}
          {(result.has_dynamic_select_explanation || dsExplanation || dsLoading || dsError) && (
            <DynamicSelectPanel
              explanation={dsExplanation}
              loading={dsLoading}
              error={dsError}
              runId={dsRunId}
              onReload={dsRunId ? () => loadDsExplanation(dsRunId) : undefined}
            />
          )}

          </>)}

          {activeResultTab === 'verification' && (
            <VerificationSuitePanel
              startDate={startDate}
              endDate={endDate}
              startingCapital={startingCapital}
              symphonyId={selectedSymphony}
            />
          )}

          {activeResultTab === 'robustness' && (
            <WalkForwardPanel
              startDate={startDate}
              endDate={endDate}
              startingCapital={startingCapital}
              symphonyId={selectedSymphony}
            />
          )}
        </>
      )}
    </div>
  );
}

interface MetricCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
  positive?: boolean;
}

function MetricCard({ icon, label, value, subtext, positive }: MetricCardProps) {
  const valueColor = positive === undefined
    ? 'text-gray-900'
    : positive
      ? 'text-emerald-700'
      : 'text-red-600';

  return (
    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
      <div className="flex items-center gap-2 text-gray-500 mb-2">
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className={`text-xl font-bold ${valueColor}`}>{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-0.5">{subtext}</p>}
    </div>
  );
}

function CompareMetricsTable({
  baseline,
  adjusted,
  formatPct,
}: {
  baseline: BacktestMetrics;
  adjusted: BacktestMetrics;
  formatPct: (v: number) => string;
}) {
  const rows: { label: string; base: string; adj: string; better?: 'higher' | 'lower' | null }[] = [
    {
      label: 'Total Return',
      base: formatPct(baseline.total_return_pct),
      adj: formatPct(adjusted.total_return_pct),
      better: adjusted.total_return_pct >= baseline.total_return_pct ? 'higher' : null,
    },
    {
      label: 'Ann. Return',
      base: formatPct(baseline.annualized_return_pct),
      adj: formatPct(adjusted.annualized_return_pct),
      better: adjusted.annualized_return_pct >= baseline.annualized_return_pct ? 'higher' : null,
    },
    {
      label: 'Max Drawdown',
      base: `-${baseline.max_drawdown_pct.toFixed(2)}%`,
      adj: `-${adjusted.max_drawdown_pct.toFixed(2)}%`,
      better: adjusted.max_drawdown_pct <= baseline.max_drawdown_pct ? 'lower' : null,
    },
    {
      label: 'Sharpe Ratio',
      base: baseline.sharpe_ratio.toFixed(2),
      adj: adjusted.sharpe_ratio.toFixed(2),
      better: adjusted.sharpe_ratio >= baseline.sharpe_ratio ? 'higher' : null,
    },
    {
      label: 'Volatility',
      base: `${baseline.volatility_pct.toFixed(2)}%`,
      adj: `${adjusted.volatility_pct.toFixed(2)}%`,
      better: adjusted.volatility_pct <= baseline.volatility_pct ? 'lower' : null,
    },
    {
      label: 'Win Rate',
      base: `${baseline.win_rate_pct.toFixed(1)}%`,
      adj: `${adjusted.win_rate_pct.toFixed(1)}%`,
      better: adjusted.win_rate_pct >= baseline.win_rate_pct ? 'higher' : null,
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Metric</th>
            <th className="text-right py-2 pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Baseline</th>
            <th className="text-right py-2 text-xs font-semibold text-emerald-600 uppercase tracking-wide">+ Multiplier</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.label} className="hover:bg-gray-50">
              <td className="py-2.5 pr-4 text-gray-700 font-medium">{row.label}</td>
              <td className="py-2.5 pr-4 text-right font-mono text-gray-500">{row.base}</td>
              <td className={`py-2.5 text-right font-mono font-semibold ${
                row.better === 'higher' || row.better === 'lower' ? 'text-emerald-700' : 'text-red-600'
              }`}>
                {row.adj}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegimeComparisonPanel({
  comparison,
  startingCapital,
  formatCurrency,
  formatPct,
}: {
  comparison: BacktestRegimeComparison;
  startingCapital: number;
  formatCurrency: (v: number) => string;
  formatPct: (v: number) => string;
}) {
  const chartData = comparison.baseline_equity_curve.map((pt) => {
    const adjPt = comparison.adjusted_equity_curve.find((a) => a.date === pt.date);
    return {
      date: pt.date,
      baseline: pt.equity,
      baselineRet: startingCapital > 0 ? ((pt.equity / startingCapital) - 1) * 100 : 0,
      adjusted: adjPt?.equity,
      adjustedRet: adjPt && startingCapital > 0 ? ((adjPt.equity / startingCapital) - 1) * 100 : undefined,
    };
  });

  const spanDays = (() => {
    if (chartData.length < 2) return 0;
    const a = new Date(chartData[0].date + 'T00:00:00').getTime();
    const b = new Date(chartData[chartData.length - 1].date + 'T00:00:00').getTime();
    return (b - a) / 86_400_000;
  })();

  const endBaseline = comparison.baseline_equity_curve[comparison.baseline_equity_curve.length - 1]?.equity ?? startingCapital;
  const endAdjusted = comparison.adjusted_equity_curve[comparison.adjusted_equity_curve.length - 1]?.equity ?? startingCapital;

  return (
    <div className="bg-white rounded-lg p-6 shadow-md border border-emerald-200">
      <h2 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
        <SlidersHorizontal className="w-5 h-5 text-emerald-600" />
        Regime Multiplier Comparison
      </h2>
      {comparison.policy_name && (
        <p className="text-xs text-gray-500 mb-4">
          Policy: <span className="font-semibold">{comparison.policy_name}</span>
          {comparison.exposure_reduction_pct > 0 && (
            <span className="ml-2">· Avg exposure reduction: {comparison.exposure_reduction_pct.toFixed(1)}%</span>
          )}
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-0.5">Baseline End Equity</p>
          <p className="font-bold text-gray-800">{formatCurrency(endBaseline)}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-emerald-600 mb-0.5">Adjusted End Equity</p>
          <p className="font-bold text-emerald-800">{formatCurrency(endAdjusted)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-0.5">Baseline Sortino</p>
          <p className="font-bold text-gray-800">{comparison.sortino_ratio_baseline.toFixed(2)}</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs text-emerald-600 mb-0.5">Adjusted Sortino</p>
          <p className="font-bold text-emerald-800">{comparison.sortino_ratio_adjusted.toFixed(2)}</p>
        </div>
      </div>

      {/* Dual equity curve */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-2 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-0.5 bg-gray-400 rounded" />
            <span>Baseline (no multiplier)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-0.5 bg-emerald-600 rounded" />
            <span>With regime multiplier</span>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="adjGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.12} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickFormatter={(v) => {
                  const d = new Date(v + 'T00:00:00');
                  return d.toLocaleDateString('en-US', spanDays > 180
                    ? { month: 'short', year: '2-digit' }
                    : { month: 'short', day: 'numeric' });
                }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                width={52}
              />
              <Tooltip
                formatter={((value: number | undefined, name: string | undefined) => {
                  if (value === undefined) return ['—', name ?? ''];
                  const fmt$ = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
                  if (name === 'baseline') return [fmt$(value), 'Baseline'];
                  if (name === 'adjusted') return [fmt$(value), 'With Multiplier'];
                  return [value, name ?? ''];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }) as any}
                labelFormatter={(label) => new Date(label + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Line type="monotone" dataKey="baseline" stroke="#9ca3af" strokeWidth={1.5} dot={false} />
              <Area type="monotone" dataKey="adjusted" stroke="#059669" strokeWidth={2} fill="url(#adjGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Metrics comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-bold text-gray-700 mb-3">Performance Metrics</p>
          <CompareMetricsTable
            baseline={comparison.baseline_metrics}
            adjusted={comparison.adjusted_metrics}
            formatPct={formatPct}
          />
        </div>
        {comparison.per_symbol_comparison && comparison.per_symbol_comparison.length > 0 && (
          <div>
            <p className="text-sm font-bold text-gray-700 mb-3">Weight Changes by Symbol</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-1.5 pr-3 text-gray-500 uppercase tracking-wide">Symbol</th>
                    <th className="text-right py-1.5 pr-3 text-gray-500 uppercase tracking-wide">Base Wt</th>
                    <th className="text-right py-1.5 pr-3 text-gray-500 uppercase tracking-wide">Adj Wt</th>
                    <th className="text-right py-1.5 text-gray-500 uppercase tracking-wide">Avg ×</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {comparison.per_symbol_comparison.slice(0, 12).map((row) => (
                    <tr key={row.symbol} className="hover:bg-gray-50">
                      <td className="py-1.5 pr-3 font-mono font-semibold text-gray-800">{row.symbol}</td>
                      <td className="py-1.5 pr-3 text-right text-gray-500">{(row.avg_baseline_weight * 100).toFixed(1)}%</td>
                      <td className={`py-1.5 pr-3 text-right font-semibold ${
                        row.avg_adjusted_weight < row.avg_baseline_weight ? 'text-amber-600' : 'text-emerald-700'
                      }`}>{(row.avg_adjusted_weight * 100).toFixed(1)}%</td>
                      <td className="py-1.5 text-right font-mono text-gray-600">{row.avg_effective_multiplier.toFixed(2)}x</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DynamicSelectPanel
// ---------------------------------------------------------------------------

type DsViewTab = 'selected' | 'rejected';

function DynamicSelectPanel({
  explanation,
  loading,
  error,
  runId,
  onReload,
}: {
  explanation: DynamicSelectExplanationEntry[] | null;
  loading: boolean;
  error: string | null;
  runId: number | null;
  onReload?: () => void;
}) {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewTab, setViewTab] = useState<DsViewTab>('selected');

  useEffect(() => {
    if (explanation && explanation.length > 0) {
      setSelectedDate(explanation[explanation.length - 1].date);
    }
  }, [explanation]);

  const activeEntry = explanation?.find((e) => e.date === selectedDate) ?? null;
  const fallbackDates = explanation?.filter((e) => e.fallback_used) ?? [];
  const regimeBlockedDates = explanation?.filter((e) => !e.market_regime_ok) ?? [];

  // Collect all component keys from selected items for stable column headers
  const componentKeys: string[] = [];
  if (activeEntry) {
    const allItems = [...(activeEntry.selected ?? []), ...(activeEntry.rejected ?? [])];
    for (const item of allItems) {
      for (const k of Object.keys(item.component_values ?? {})) {
        if (!componentKeys.includes(k)) componentKeys.push(k);
      }
    }
  }

  const csvUrl = runId ? `/api/backtest/result/${runId}/dynamic-select-explanation.csv` : '#';

  return (
    <div className="bg-white rounded-lg shadow-md border border-indigo-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-indigo-100 bg-indigo-50">
        <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
          <Layers className="w-4 h-4 text-indigo-600" />
          Dynamic Selection
        </h2>
        <div className="flex items-center gap-3">
          {runId && (
            <a
              href={csvUrl}
              download
              className="flex items-center gap-1.5 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 hover:bg-indigo-50 rounded px-2.5 py-1 transition-colors"
            >
              <Download className="w-3 h-3" />
              Export CSV
            </a>
          )}
          {onReload && (
            <button
              onClick={onReload}
              disabled={loading}
              className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Reload
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {loading && (
          <div className="flex items-center gap-2 text-gray-500 text-sm py-4">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading dynamic selection data…
          </div>
        )}

        {error && !loading && (
          <div className="flex items-center gap-2 text-red-600 text-sm py-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {explanation && !loading && (
          <>
            {/* Summary strip */}
            <div className="flex items-center gap-4 mb-4 flex-wrap text-xs text-gray-500">
              <span>
                <span className="font-semibold text-gray-700">{explanation.length}</span> trading days
              </span>
              {fallbackDates.length > 0 && (
                <span className="text-amber-600 font-medium">
                  ⚠ Fallback on {fallbackDates.length} day{fallbackDates.length !== 1 ? 's' : ''}
                </span>
              )}
              {regimeBlockedDates.length > 0 && (
                <span className="text-orange-600 font-medium">
                  🛡 Regime gate triggered {regimeBlockedDates.length}×
                </span>
              )}
              {runId && <span className="text-gray-400">Run #{runId}</span>}
            </div>

            {/* Date pills — last 30 */}
            <div className="flex flex-wrap gap-1 mb-5">
              {explanation.slice(-30).map((e) => (
                <button
                  key={e.date}
                  onClick={() => setSelectedDate(e.date)}
                  className={`px-2 py-0.5 rounded text-xs font-mono border transition-colors ${
                    selectedDate === e.date
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : !e.market_regime_ok
                      ? 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100'
                      : e.fallback_used
                      ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  {e.date.slice(5)}
                </button>
              ))}
            </div>

            {activeEntry && (
              <div>
                {/* Date header + regime badge */}
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="text-sm font-bold text-gray-800">{activeEntry.date}</span>
                  {!activeEntry.market_regime_ok && (
                    <span className="text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                      regime gate — fallback
                    </span>
                  )}
                  {activeEntry.fallback_used && activeEntry.market_regime_ok && (
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                      fallback
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {activeEntry.selected.length} selected · {activeEntry.eligible_count} eligible · {activeEntry.universe_count} universe
                  </span>
                </div>

                {/* Selected / Rejected tab switcher */}
                <div className="flex gap-0 mb-4 border border-gray-200 rounded-md w-fit overflow-hidden text-xs font-medium">
                  {(['selected', 'rejected'] as DsViewTab[]).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setViewTab(tab)}
                      className={`px-3 py-1.5 capitalize transition-colors ${
                        viewTab === tab
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {tab}
                      <span className={`ml-1.5 rounded-full px-1 py-0.5 text-[10px] font-bold ${
                        viewTab === tab ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {tab === 'selected' ? activeEntry.selected.length : activeEntry.rejected.length}
                      </span>
                    </button>
                  ))}
                </div>

                {/* ── Selected symbols ── */}
                {viewTab === 'selected' && (
                  <div className="overflow-x-auto">
                    {activeEntry.selected.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No symbols selected — fallback active.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Symbol</th>
                            <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Weight</th>
                            <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Score</th>
                            <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rank</th>
                            <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Prev Wt</th>
                            {componentKeys.map((k) => (
                              <th key={k} className="text-right py-2 pr-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                                {k.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {activeEntry.selected.map((item) => (
                            <tr key={item.symbol} className="hover:bg-green-50">
                              <td className="py-2 pr-3 font-mono font-semibold text-gray-800 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-green-500 inline-block flex-shrink-0" />
                                {item.symbol}
                              </td>
                              <td className="py-2 pr-3 text-right font-medium text-gray-700">
                                {(item.weight * 100).toFixed(1)}%
                              </td>
                              <td className="py-2 pr-3 text-right font-mono text-indigo-700">
                                {item.composite_score.toFixed(4)}
                              </td>
                              <td className="py-2 pr-3 text-right font-mono text-indigo-500">
                                {(item.composite_rank * 100).toFixed(0)}th
                              </td>
                              <td className="py-2 pr-3 text-right font-mono text-gray-400">
                                {item.prev_weight > 0 ? `${(item.prev_weight * 100).toFixed(1)}%` : '—'}
                              </td>
                              {componentKeys.map((k) => {
                                const val = item.component_values?.[k];
                                const rank = item.component_ranks?.[k];
                                return (
                                  <td key={k} className="py-2 pr-3 text-right font-mono text-gray-500 whitespace-nowrap">
                                    {val != null ? val.toFixed(4) : '—'}
                                    {rank != null && (
                                      <span className="ml-1 text-gray-300 text-[10px]">
                                        ({(rank * 100).toFixed(0)})
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                {/* ── Rejected symbols ── */}
                {viewTab === 'rejected' && (
                  <div className="overflow-x-auto">
                    {activeEntry.rejected.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No symbols rejected.</p>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Symbol</th>
                            <th className="text-left py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Reason</th>
                            <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Score</th>
                            <th className="text-right py-2 pr-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rank</th>
                            {componentKeys.map((k) => (
                              <th key={k} className="text-right py-2 pr-3 text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                                {k.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {activeEntry.rejected.map((item) => {
                            const badgeColor =
                              item.reason === 'filter'
                                ? 'bg-red-50 text-red-700 border-red-200'
                                : item.reason === 'rank_out'
                                ? 'bg-gray-50 text-gray-600 border-gray-200'
                                : item.reason === 'market_regime_filter'
                                ? 'bg-orange-50 text-orange-700 border-orange-200'
                                : 'bg-yellow-50 text-yellow-700 border-yellow-200';
                            return (
                              <tr key={item.symbol} className="hover:bg-red-50">
                                <td className="py-2 pr-3 font-mono font-semibold text-gray-700 flex items-center gap-1.5">
                                  <span className={`w-2 h-2 rounded-full inline-block flex-shrink-0 ${
                                    item.reason === 'rank_out' ? 'bg-gray-400' : 'bg-red-400'
                                  }`} />
                                  {item.symbol}
                                </td>
                                <td className="py-2 pr-3">
                                  <span className={`text-xs font-medium border rounded px-1.5 py-0.5 ${badgeColor}`}>
                                    {item.reason.replace(/_/g, ' ')}
                                  </span>
                                  {item.filter_results && (
                                    <span className="ml-1.5 text-xs text-gray-400">
                                      ({Object.entries(item.filter_results).map(([k, v]) => `${k}:${v ? '✓' : '✗'}`).join(' ')})
                                    </span>
                                  )}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-gray-500">
                                  {item.composite_score != null ? item.composite_score.toFixed(4) : '—'}
                                </td>
                                <td className="py-2 pr-3 text-right font-mono text-gray-400">
                                  {item.composite_rank != null ? `${(item.composite_rank * 100).toFixed(0)}th` : '—'}
                                </td>
                                {componentKeys.map((k) => {
                                  const val = item.component_values?.[k];
                                  const rank = item.component_ranks?.[k];
                                  return (
                                    <td key={k} className="py-2 pr-3 text-right font-mono text-gray-400 whitespace-nowrap">
                                      {val != null ? val.toFixed(4) : '—'}
                                      {rank != null && (
                                        <span className="ml-1 text-gray-300 text-[10px]">
                                          ({(rank * 100).toFixed(0)})
                                        </span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verification Suite Panel
// ---------------------------------------------------------------------------

const VERIF_MODE_LABELS: Record<string, string> = {
  LEGACY_SAME_CLOSE:            'Legacy (same close)',
  PREV_CLOSE_SIGNAL_NEXT_CLOSE: '1d lag → next close',
  PREV_CLOSE_SIGNAL_NEXT_OPEN:  '1d lag → next open',
  LAG_2D_NEXT_CLOSE:            '2d lag → next close',
  LAG_2D_NEXT_OPEN:             '2d lag → next open',
};

const VERIF_OVERLAY_KEYS = [
  { key: 'legacy',   mode: 'LEGACY_SAME_CLOSE',            label: 'Legacy',          stroke: '#059669', dash: undefined },
  { key: 'nc1d',     mode: 'PREV_CLOSE_SIGNAL_NEXT_CLOSE', label: '1d lag → close',  stroke: '#6366f1', dash: undefined },
  { key: 'no1d',     mode: 'PREV_CLOSE_SIGNAL_NEXT_OPEN',  label: '1d lag → open',   stroke: '#8b5cf6', dash: '5 3' },
  { key: 'nc2d',     mode: 'LAG_2D_NEXT_CLOSE',            label: '2d lag → close',  stroke: '#f59e0b', dash: undefined },
  { key: 'no2d',     mode: 'LAG_2D_NEXT_OPEN',             label: '2d lag → open',   stroke: '#ef4444', dash: '5 3' },
] as const;

function VerificationSuitePanel({
  startDate,
  endDate,
  startingCapital,
  symphonyId,
}: {
  startDate: string;
  endDate: string;
  startingCapital: string;
  symphonyId: string;
}) {
  const [runId, setRunId] = useState<number | null>(null);
  const [status, setStatus] = useState<'idle' | 'running' | 'complete' | 'error'>('idle');
  const [verifResult, setVerifResult] = useState<VerificationSuiteResult | null>(null);
  const [verifError, setVerifError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showDataQuality, setShowDataQuality] = useState(false);
  const [showCacheDiag, setShowCacheDiag] = useState(false);
  const [cacheDiag, setCacheDiag] = useState<import('../types/api').VerifyCacheDiagnostics | null>(null);
  const [cacheDiagLoading, setCacheDiagLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [includeRealisticTier, setIncludeRealisticTier] = useState(false);
  const [missingDataPolicy, setMissingDataPolicy] = useState<string>('fill_forward_with_warning');
  const [strictLookahead, setStrictLookahead] = useState(false);
  const [requireNextOpenProfitable, setRequireNextOpenProfitable] = useState(true);
  const [maxDdThreshold, setMaxDdThreshold] = useState<string>('15');
  const [minCagrAfter5bps, setMinCagrAfter5bps] = useState<string>('20');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showRunSettings, setShowRunSettings] = useState(false);
  const [lastSubmitParams, setLastSubmitParams] = useState<{
    scenarioMatrix: string[];
    missingDataPolicy: string;
    thresholds: Record<string, number | boolean>;
  } | null>(null);

  const stopTimers = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
  };
  useEffect(() => () => stopTimers(), []);

  const handleRun = async () => {
    stopTimers();
    setStatus('running');
    setVerifResult(null);
    setVerifError(null);
    setElapsed(0);
    setRunId(null);

    try {
      const scenarioMatrix: string[] = [];
      if (includeRealisticTier) {
        scenarioMatrix.push(
          'LEGACY_SAME_CLOSE',
          'PREV_CLOSE_SIGNAL_NEXT_CLOSE',
          'PREV_CLOSE_SIGNAL_NEXT_OPEN',
          'LAG_2D_NEXT_CLOSE',
          'LAG_2D_NEXT_OPEN',
          'REALISTIC_TIER_COST',
          'REALISTIC_TIER_COST_PLUS_2BPS',
          'REALISTIC_TIER_COST_PLUS_5BPS',
        );
      }

      const thresholdsOverride: Record<string, number | boolean> = {
        fail_on_lookahead_detected: strictLookahead,
        require_next_open_profitable: requireNextOpenProfitable,
        max_dd_limit: parseFloat(maxDdThreshold) || 15,
        min_cagr_after_5bps: parseFloat(minCagrAfter5bps) || 20,
      };

      setLastSubmitParams({
        scenarioMatrix,
        missingDataPolicy,
        thresholds: thresholdsOverride,
      });

      const sub = await api.submitVerification({
        start_date: startDate,
        end_date: endDate,
        starting_capital: parseFloat(startingCapital) || 100000,
        symphony_id: symphonyId !== 'combined' ? symphonyId : undefined,
        scenario_matrix: scenarioMatrix.length > 0 ? scenarioMatrix : undefined,
        thresholds: thresholdsOverride,
        missing_data_policy: missingDataPolicy,
      });
      setRunId(sub.run_id);
      elapsedRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
      pollRef.current = setInterval(async () => {
        try {
          const poll = await api.getVerificationStatus(sub.run_id);
          if (poll.status === 'complete') {
            stopTimers();
            setStatus('complete');
            setVerifResult(poll.result ?? null);
          } else if (poll.status === 'error') {
            stopTimers();
            setStatus('error');
            setVerifError(poll.error ?? 'Verification failed.');
          }
        } catch { /* transient poll errors ignored */ }
      }, 2500);
    } catch (err) {
      stopTimers();
      setStatus('error');
      setVerifError(err instanceof Error ? err.message : 'Failed to submit verification.');
    }
  };

  // Merge 0bps equity curves for all 5 modes into a single recharts dataset
  const overlayData = useMemo(() => {
    if (!verifResult) return [];
    const cap = verifResult.starting_capital || 100000;
    const sc  = verifResult.scenarios;
    const map = new Map<string, Record<string, unknown>>();

    VERIF_OVERLAY_KEYS.forEach(({ key, mode }) => {
      const curve = sc.find(s => s.execution_mode === mode && s.cost_bps === 0)?.equity_curve ?? [];
      curve.forEach(p => {
        const row = map.get(p.date) ?? { date: p.date };
        (row as Record<string, unknown>)[key] = parseFloat((((p.equity / cap) - 1) * 100).toFixed(2));
        map.set(p.date, row);
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.date as string).localeCompare(b.date as string)
    );
  }, [verifResult]);

  const verdictColor = verifResult?.verdict === 'PASS'
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : verifResult?.verdict === 'WARNING'
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : 'bg-red-100 text-red-800 border-red-300';

  const badgeCls = (s: string) =>
    s === 'PASS'    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : s === 'FAIL'  ? 'bg-red-100 text-red-700 border-red-200'
    : s === 'INVALID' ? 'bg-gray-100 text-gray-500 border-gray-300'
    : 'bg-amber-100 text-amber-700 border-amber-200';

  const handleShowCacheDiag = async () => {
    if (!runId) return;
    setShowCacheDiag(v => !v);
    if (cacheDiag || cacheDiagLoading) return;
    setCacheDiagLoading(true);
    try {
      const d = await api.getVerificationCacheDiagnostics(runId);
      setCacheDiag(d);
    } catch { /* ignore */ }
    finally { setCacheDiagLoading(false); }
  };

  const rowBg = (mode: string) =>
    mode.startsWith('LEGACY')     ? ''
    : mode.includes('NEXT_CLOSE') ? 'bg-blue-50/30'
    : 'bg-violet-50/30';

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-violet-600" />
          Verification Suite
        </h2>
        <div className="flex items-center gap-2">
          {status === 'complete' && verifResult && runId && (
            <a
              href={api.getVerificationExportUrl(runId)}
              download
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 border border-gray-300 hover:border-emerald-300 rounded-lg transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export CSV
            </a>
          )}
          <button
            onClick={handleRun}
            disabled={status === 'running'}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {status === 'running'
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Running… {elapsed}s</>
              : <><Play className="w-4 h-4" /> Run Verification</>}
          </button>
        </div>
      </div>

      {/* Advanced Options */}
      <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50">
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Advanced options
          {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>

        {showAdvanced && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {/* Realistic tier scenarios toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeRealisticTier}
                onChange={e => setIncludeRealisticTier(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-xs text-gray-700">Include realistic tier-cost scenarios</span>
            </label>

            {/* Strict lookahead toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={strictLookahead}
                onChange={e => setStrictLookahead(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-xs text-gray-700">Strict lookahead mode (LEGACY is a hard gate)</span>
            </label>

            {/* Require next-open profitable toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requireNextOpenProfitable}
                onChange={e => setRequireNextOpenProfitable(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="text-xs text-gray-700">Require next-open scenario profitable</span>
            </label>

            {/* Missing-data policy */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Missing-data policy</label>
              <select
                value={missingDataPolicy}
                onChange={e => setMissingDataPolicy(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-violet-500 focus:border-violet-500"
              >
                <option value="fill_forward_with_warning">Fill-forward with warning (default)</option>
                <option value="skip_trade">Skip trade on stale bars</option>
                <option value="fail">Fail on any stale bar</option>
              </select>
            </div>

            {/* Max DD threshold */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Max drawdown threshold (%)</label>
              <input
                type="number"
                min="1"
                max="100"
                step="1"
                value={maxDdThreshold}
                onChange={e => setMaxDdThreshold(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-violet-500 focus:border-violet-500 w-24"
              />
            </div>

            {/* Min CAGR after 5bps */}
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600">Min CAGR after 5 bps (%)</label>
              <input
                type="number"
                min="0"
                max="200"
                step="1"
                value={minCagrAfter5bps}
                onChange={e => setMinCagrAfter5bps(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-violet-500 focus:border-violet-500 w-24"
              />
            </div>
          </div>
        )}
      </div>

      {/* Idle prompt */}
      {status === 'idle' && (
        <div className="px-6 py-8 text-center text-gray-400 text-sm">
          Stress-test execution timing and costs across{' '}
          <span className="font-semibold text-gray-600">
            {includeRealisticTier ? '23 scenarios' : '20 scenarios'}
          </span>{' '}
          ({includeRealisticTier ? '5 timing modes × 4 cost levels + 3 realistic-tier' : '5 timing modes × 4 cost levels'}) to get a deployment readiness verdict.
        </div>
      )}

      {/* Running */}
      {status === 'running' && (
        <div className="px-6 py-10 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
          <p className="text-sm">Running 20 scenarios — takes roughly 1.5× a full backtest…</p>
          <p className="text-xs text-gray-400">{elapsed}s elapsed</p>
        </div>
      )}

      {/* Error */}
      {status === 'error' && (
        <div className="px-6 py-4">
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{verifError}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {status === 'complete' && verifResult && (
        <div className="px-6 pb-6 space-y-6 pt-4">

          {/* Verdict card */}
          <div className={`flex items-start gap-4 border rounded-xl p-4 ${verdictColor}`}>
            <div className={`text-2xl font-black tracking-wide min-w-fit ${
              verifResult.verdict === 'PASS' ? 'text-emerald-700'
              : verifResult.verdict === 'WARNING' ? 'text-amber-700'
              : 'text-red-700'
            }`}>
              {verifResult.verdict}
            </div>
            <div className="flex-1 min-w-0">
              <ul className="space-y-1">
                {verifResult.verdict_reasons.map((r, i) => (
                  <li key={i} className="text-sm leading-relaxed">{r}</li>
                ))}
              </ul>
              <p className="text-xs opacity-70 mt-2">
                Reference CAGR (legacy 0bps): {verifResult.reference_cagr_pct.toFixed(1)}%
                {' · '}{verifResult.scenarios.length} scenarios
                {' · '}Completed in {verifResult.elapsed_seconds.toFixed(1)}s
              </p>
            </div>
          </div>

          {/* Run settings used */}
          {lastSubmitParams && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                onClick={() => setShowRunSettings(v => !v)}
              >
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1.5">
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Run settings used
                </span>
                {showRunSettings ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
              </button>
              {showRunSettings && (
                <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs text-gray-700 bg-white">
                  <div>
                    <span className="text-gray-500 font-medium">Scenario matrix:</span>{' '}
                    {lastSubmitParams.scenarioMatrix.length > 0
                      ? lastSubmitParams.scenarioMatrix.join(', ')
                      : 'Standard 20 (5 modes × 4 cost levels)'}
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Missing-data policy:</span>{' '}
                    <span className={
                      lastSubmitParams.missingDataPolicy === 'fail' ? 'text-red-600 font-semibold' :
                      lastSubmitParams.missingDataPolicy === 'skip_trade' ? 'text-amber-600' :
                      'text-gray-700'
                    }>
                      {lastSubmitParams.missingDataPolicy}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Max drawdown limit:</span>{' '}
                    {String(lastSubmitParams.thresholds.max_dd_limit)}%
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Min CAGR after 5bps:</span>{' '}
                    {String(lastSubmitParams.thresholds.min_cagr_after_5bps)}%
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Strict lookahead gate:</span>{' '}
                    {lastSubmitParams.thresholds.fail_on_lookahead_detected ? <span className="text-amber-700 font-semibold">on</span> : <span className="text-gray-400">off</span>}
                  </div>
                  <div>
                    <span className="text-gray-500 font-medium">Require next-open profitable:</span>{' '}
                    {lastSubmitParams.thresholds.require_next_open_profitable ? <span className="text-emerald-700">yes</span> : <span className="text-gray-400">no</span>}
                  </div>
                  {verifResult.cache_key_components && (
                    <>
                      <div>
                        <span className="text-gray-500 font-medium">Engine version:</span>{' '}
                        <span className="font-mono">{String(verifResult.cache_key_components.engine_version ?? '—')}</span>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-gray-500 font-medium">Input hash:</span>{' '}
                        <span className="font-mono text-gray-600 break-all">
                          {verifResult.cache_key_components.input_hash
                            ? String(verifResult.cache_key_components.input_hash).slice(0, 16) + '…'
                            : '—'}
                          {' '}
                          <span className="text-gray-400 not-italic">(full hash in Cache Diagnostics)</span>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Scenario table */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Scenario Comparison ({verifResult.scenarios.length} scenarios)</h3>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Execution Mode</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Cost</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">CAGR</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Total Ret</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Max DD</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Sharpe</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Sortino</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Worst Mo</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap" title="Annualized turnover — total gross notional traded ÷ avg portfolio value × 100">Ann. TO</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap" title="Gross traded notional ($)">Notional</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap" title="Transaction costs as % of gross profit (lower is better)">Cost/Pft</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">End Equity</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Cost $</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap" title="Top cost symbol — highest cumulative transaction cost">Top Cost</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Trades</th>
                    <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">vs Legacy</th>
                    <th className="text-center py-2.5 px-3 font-semibold text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {verifResult.scenarios.map((s, i) => {
                    const isInvalid = s.scenario_status === 'INVALID';
                    const topCostSym = s.top_cost_symbols?.[0];
                    return (
                      <tr key={i} className={`hover:bg-gray-50/80 ${isInvalid ? 'opacity-50' : rowBg(s.execution_mode)}`}>
                        <td className="py-2 px-3 text-gray-700 whitespace-nowrap font-medium">
                          <div className="flex items-center gap-1.5">
                            {VERIF_MODE_LABELS[s.execution_mode] ?? s.execution_mode}
                            {s.lookahead_audit?.lookahead_detected && (
                              <span
                                title={`Lookahead bias: signal_date=${s.lookahead_audit?.signal_date ?? '?'} == execution_date=${s.lookahead_audit?.execution_date ?? '?'} (same-day close)`}
                                className="text-amber-500 cursor-help"
                              >⚠</span>
                            )}
                          </div>
                          {isInvalid && s.fail_reason && (
                            <div className="text-xs text-gray-400 mt-0.5 max-w-xs truncate" title={s.fail_reason}>
                              {s.fail_reason.slice(0, 60)}…
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500">{s.cost_bps}bps</td>
                        <td className={`py-2 px-3 text-right font-semibold ${isInvalid ? 'text-gray-400' : s.cagr_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {isInvalid ? '—' : `${s.cagr_pct >= 0 ? '+' : ''}${s.cagr_pct.toFixed(1)}%`}
                        </td>
                        <td className={`py-2 px-3 text-right ${isInvalid ? 'text-gray-400' : s.total_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {isInvalid ? '—' : `${s.total_return_pct >= 0 ? '+' : ''}${s.total_return_pct.toFixed(1)}%`}
                        </td>
                        <td className={`py-2 px-3 text-right ${isInvalid ? 'text-gray-400' : 'text-red-600'}`}>
                          {isInvalid ? '—' : `-${s.max_drawdown_pct.toFixed(1)}%`}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {isInvalid ? '—' : s.sharpe_ratio.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">
                          {isInvalid ? '—' : (s.sortino_ratio === Infinity ? '∞' : s.sortino_ratio.toFixed(2))}
                        </td>
                        <td className={`py-2 px-3 text-right ${isInvalid ? 'text-gray-400' : s.worst_month_pct < -5 ? 'text-red-600' : 'text-gray-600'}`}>
                          {isInvalid ? '—' : `${s.worst_month_pct.toFixed(1)}%`}
                        </td>
                        {/* ── New turnover / notional / cost-share columns ── */}
                        <td className={`py-2 px-3 text-right ${isInvalid ? 'text-gray-400' : s.annualized_turnover_pct > 500 ? 'text-amber-600' : 'text-gray-500'}`}>
                          {isInvalid || !s.annualized_turnover_pct ? '—' : `${s.annualized_turnover_pct.toFixed(0)}%`}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500 font-mono text-xs">
                          {isInvalid || !s.gross_traded_notional ? '—' : `$${(s.gross_traded_notional / 1000).toFixed(0)}k`}
                        </td>
                        <td className={`py-2 px-3 text-right ${isInvalid ? 'text-gray-400' : s.cost_as_pct_gross_profit == null ? 'text-gray-400' : s.cost_as_pct_gross_profit > 20 ? 'text-red-600' : 'text-gray-500'}`}>
                          {isInvalid || s.cost_as_pct_gross_profit == null ? '—' : `${s.cost_as_pct_gross_profit.toFixed(1)}%`}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-700 font-mono">
                          {isInvalid ? '—' : `$${Math.round(s.ending_equity).toLocaleString('en-US')}`}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500">
                          {isInvalid ? '—' : s.total_cost_usd > 0
                            ? `$${Math.round(s.total_cost_usd).toLocaleString('en-US')}`
                            : '—'}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500 whitespace-nowrap text-xs"
                            title={topCostSym ? `${topCostSym.symbol}: $${topCostSym.total_cost_usd.toFixed(2)} total cost` : ''}>
                          {isInvalid || !topCostSym ? '—'
                            : <><span className="font-mono font-medium">{topCostSym.symbol}</span><span className="text-gray-400 ml-1">${Math.round(topCostSym.total_cost_usd).toLocaleString()}</span></>}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-400">
                          {isInvalid ? '—' : s.trade_count > 0 ? s.trade_count.toLocaleString() : '—'}
                        </td>
                        <td className={`py-2 px-3 text-right font-medium ${
                          isInvalid || s.degradation_vs_legacy_pct == null ? 'text-gray-400'
                          : s.degradation_vs_legacy_pct >= 0 ? 'text-emerald-600'
                          : 'text-red-600'
                        }`}>
                          {isInvalid || s.degradation_vs_legacy_pct == null ? '—'
                            : `${s.degradation_vs_legacy_pct >= 0 ? '+' : ''}${s.degradation_vs_legacy_pct.toFixed(1)}%`}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <span className={`text-xs font-semibold border rounded px-1.5 py-0.5 ${badgeCls(s.scenario_status)}`}>
                            {s.scenario_status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              ⚠ = lookahead bias (signal_date == execution_date, same-day close); Ann. TO = annualized turnover; Cost/Pft = cost as % of gross profit; INVALID = open prices unavailable
            </p>
          </div>

          {/* Data Quality Panel */}
          {verifResult.data_quality && verifResult.data_quality.length > 0 && (
            <div>
              <button
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-violet-700 transition-colors mb-3"
                onClick={() => setShowDataQuality(v => !v)}
              >
                <ShieldCheck className="w-4 h-4" />
                Data Quality ({verifResult.data_quality.length} symbols
                {verifResult.data_quality.filter(r => r.is_stale || r.missing_trading_days > 10 || r.fill_forward_warning).length > 0
                  ? <span className="ml-1 text-amber-600">· {verifResult.data_quality.filter(r => r.is_stale || r.missing_trading_days > 10 || r.fill_forward_warning).length} with issues</span>
                  : null}
                )
                {showDataQuality ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showDataQuality && (
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 font-semibold text-gray-500">Symbol</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-500">First Date</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-500">Last Date</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-500">Expected Days</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-500">Actual Days</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-500">Missing</th>
                        <th className="text-right py-2 px-3 font-semibold text-gray-500">Fill-Fwd Bars</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-500">Policy Applied</th>
                        <th className="text-center py-2 px-3 font-semibold text-gray-500">Flags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {verifResult.data_quality.map((r, i) => {
                        const hasIssue = r.is_stale || r.missing_trading_days > 10 || r.forward_filled_bars > 0;
                        const policyColor =
                          r.missing_data_policy_applied === 'fail' ? 'text-red-600 font-semibold' :
                          r.missing_data_policy_applied === 'skip_trade' ? 'text-amber-600 font-medium' :
                          r.missing_data_policy_applied === 'fill_forward_with_warning' ? 'text-amber-500' :
                          'text-gray-400';
                        const policyLabel =
                          r.missing_data_policy_applied === 'fail' ? 'FAIL' :
                          r.missing_data_policy_applied === 'skip_trade' ? 'SKIP' :
                          r.missing_data_policy_applied === 'fill_forward_with_warning' ? 'FF-WARN' :
                          '—';
                        const policyTitle =
                          r.missing_data_policy_applied === 'fail' ? 'Policy: fail — fill-forward bars cause suite abort' :
                          r.missing_data_policy_applied === 'skip_trade' ? `Policy: skip_trade — trades skipped on ${r.ff_bar_dates?.length ?? 0} fill-forward date(s)` :
                          r.missing_data_policy_applied === 'fill_forward_with_warning' ? 'Policy: fill_forward_with_warning — stale prices used with warning' :
                          'No fill-forward bars detected';
                        return (
                          <tr key={i} className={hasIssue ? 'bg-amber-50/40' : ''}>
                            <td className="py-1.5 px-3 font-mono font-medium text-gray-800">{r.symbol}</td>
                            <td className="py-1.5 px-3 text-right text-gray-500">{r.first_available_date ?? '—'}</td>
                            <td className={`py-1.5 px-3 text-right ${r.is_stale ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{r.last_available_date ?? '—'}</td>
                            <td className="py-1.5 px-3 text-right text-gray-400">{r.expected_trading_days}</td>
                            <td className="py-1.5 px-3 text-right text-gray-600">{r.actual_trading_days}</td>
                            <td className={`py-1.5 px-3 text-right ${r.missing_trading_days > 10 ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>
                              {r.missing_trading_days > 0 ? r.missing_trading_days : '—'}
                            </td>
                            <td className={`py-1.5 px-3 text-right ${r.fill_forward_warning ? 'text-red-600 font-medium' : r.forward_filled_bars > 0 ? 'text-amber-500' : 'text-gray-400'}`}>
                              {r.forward_filled_bars > 0 ? r.forward_filled_bars : '—'}
                            </td>
                            <td className={`py-1.5 px-3 text-center ${policyColor}`} title={policyTitle}>
                              {policyLabel}
                            </td>
                            <td className="py-1.5 px-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {r.is_stale && <span className="text-red-500 text-xs" title="Stale: last price is too old">STALE</span>}
                                {r.fill_forward_warning && <span className="text-orange-500 text-xs" title="Fill-forward on leveraged/vol ETP — upgraded to fail policy">ETP-FF</span>}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Cache Diagnostics Panel */}
          {runId && (
            <div>
              <button
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-violet-600 transition-colors"
                onClick={handleShowCacheDiag}
              >
                <span className="font-mono text-xs">🔑</span>
                Cache Diagnostics
                {showCacheDiag ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {showCacheDiag && (
                <div className="mt-2 bg-gray-50 rounded-lg border border-gray-200 p-4 text-xs space-y-2">
                  {cacheDiagLoading && <p className="text-gray-400">Loading…</p>}
                  {cacheDiag && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-gray-500">Run ID:</span>{' '}
                          <span className="font-mono text-gray-800">{cacheDiag.run_id}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Engine:</span>{' '}
                          <span className="font-mono text-gray-800">{cacheDiag.engine_version}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Status:</span>{' '}
                          <span className="font-semibold text-gray-800">{cacheDiag.status}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Cache siblings:</span>{' '}
                          <span className="text-gray-800">{cacheDiag.cache_siblings_count}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-500">Input hash:</span>{' '}
                        <span className="font-mono text-gray-700 break-all">{cacheDiag.input_hash ?? '—'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Created:</span>{' '}
                        <span className="text-gray-700">{cacheDiag.created_at ? new Date(cacheDiag.created_at).toLocaleString() : '—'}</span>
                      </div>
                      <ul className="text-gray-400 space-y-0.5 mt-1">
                        {cacheDiag.cache_notes.map((n, i) => <li key={i}>• {n}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Equity curve overlay — all 5 modes at 0bps */}
          {overlayData.length > 0 && (
            <div>
              <button
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-violet-700 transition-colors mb-3"
                onClick={() => setShowOverlay(v => !v)}
              >
                <BarChart3 className="w-4 h-4" />
                0bps Return Overlay — 5 execution modes
                {showOverlay ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showOverlay && (
                <>
                  <div className="flex flex-wrap items-center gap-4 mb-2 text-xs text-gray-500">
                    {VERIF_OVERLAY_KEYS.map(({ key, label, stroke, dash }) => (
                      <div key={key} className="flex items-center gap-1.5">
                        <div className="w-5 h-0.5" style={{
                          borderTop: `2px ${dash ? 'dashed' : 'solid'} ${stroke}`,
                        }} />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={overlayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          tickFormatter={(v) => {
                            const d = new Date(v + 'T00:00:00');
                            return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                          }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fontSize: 10, fill: '#6b7280' }}
                          tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v.toFixed(0)}%`}
                          width={54}
                        />
                        <Tooltip
                          formatter={(v: number | undefined, name: string | undefined) => {
                            const found = VERIF_OVERLAY_KEYS.find(k => k.key === name);
                            if (v == null) return ['—', found?.label ?? name ?? ''];
                            return [`${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, found?.label ?? name ?? ''];
                          }}
                          labelFormatter={(l) => new Date(l + 'T00:00:00').toLocaleDateString('en-US', {
                            month: 'long', day: 'numeric', year: 'numeric',
                          })}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        />
                        {VERIF_OVERLAY_KEYS.map(({ key, stroke, dash }) => (
                          <Line
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={stroke}
                            strokeWidth={key === 'legacy' ? 2 : 1.5}
                            strokeDasharray={dash}
                            dot={false}
                            activeDot={{ r: 3 }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Walk-Forward Panel
// ---------------------------------------------------------------------------

function fmtPct(v: number) {
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}
function fmtPctBare(v: number) {
  return `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function PeriodMetricCard({ period, label }: { period: WalkForwardPeriod; label: string }) {
  const cagr = period.annualized_return_pct;
  const mdd = period.max_drawdown_pct;
  const calmar = mdd > 0 ? cagr / mdd : null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex-1 min-w-[200px]">
      <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">{label}</div>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Period</span>
          <span className="font-mono text-xs text-gray-600">{period.start_date.slice(0, 7)} → {period.end_date.slice(0, 7)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">CAGR</span>
          <span className={`font-semibold ${cagr >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtPct(cagr)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Total Return</span>
          <span className={`font-medium ${period.total_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtPct(period.total_return_pct)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Max Drawdown</span>
          <span className="text-red-600 font-medium">-{mdd.toFixed(2)}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Sharpe</span>
          <span className="text-gray-700">{period.sharpe_ratio.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Calmar</span>
          <span className={`${calmar === null ? 'text-gray-400' : calmar >= 1 ? 'text-emerald-700' : 'text-amber-600'}`}>
            {calmar !== null ? calmar.toFixed(2) : '—'}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Worst Day</span>
          <span className="text-red-600">{period.worst_day_pct.toFixed(2)}%</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Trades</span>
          <span className="text-gray-600">{period.total_trades.toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Trading Days</span>
          <span className="text-gray-600">{period.total_trading_days}</span>
        </div>
      </div>
    </div>
  );
}

interface CustomStressPeriod {
  label: string;
  start_date: string;
  end_date: string;
}

function WalkForwardPanel({
  startDate,
  endDate,
  startingCapital,
  symphonyId,
}: {
  startDate: string;
  endDate: string;
  startingCapital: string;
  symphonyId: string;
}) {
  const dateRangeDays = startDate && endDate
    ? Math.floor((new Date(endDate).getTime() - new Date(startDate).getTime()) / 86_400_000)
    : 0;
  const dateRangeTooShort = dateRangeDays < 30;

  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<WalkForwardResult | null>(null);
  const [wfError, setWfError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [splitDate, setSplitDate] = useState('');
  const [customStress, setCustomStress] = useState<CustomStressPeriod[]>([]);
  const [newLabel, setNewLabel] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [showStressForm, setShowStressForm] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [wfRunId, setWfRunId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimers = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopTimers(), []);

  const handleAddStress = () => {
    if (!newStart || !newEnd) return;
    setCustomStress(prev => [...prev, {
      label: newLabel || `${newStart} → ${newEnd}`,
      start_date: newStart,
      end_date: newEnd,
    }]);
    setNewLabel(''); setNewStart(''); setNewEnd('');
    setShowStressForm(false);
  };

  const handleRemoveStress = (idx: number) =>
    setCustomStress(prev => prev.filter((_, i) => i !== idx));

  const buildParams = () => ({
    start_date: startDate,
    end_date: endDate,
    starting_capital: parseFloat(startingCapital) || 100000,
    symphony_id: symphonyId !== 'combined' ? symphonyId : undefined,
    split_date: splitDate || undefined,
    custom_stress_periods: customStress.length > 0 ? customStress : undefined,
  });

  const handleRun = async () => {
    stopTimers();
    setStatus('running');
    setResult(null);
    setWfError(null);
    setWfRunId(null);
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);

    try {
      const sub = await api.submitWalkForward(buildParams());
      setWfRunId(sub.run_id);
      pollRef.current = setInterval(async () => {
        try {
          const poll = await api.getWalkForwardStatus(sub.run_id);
          if (poll.status === 'complete') {
            stopTimers();
            setResult(poll.result ?? null);
            setStatus('done');
          } else if (poll.status === 'error') {
            stopTimers();
            setStatus('error');
            setWfError(poll.error ?? 'Walk-forward analysis failed.');
          }
        } catch { /* transient poll errors ignored */ }
      }, 3000);
    } catch (err) {
      stopTimers();
      setStatus('error');
      setWfError(err instanceof Error ? err.message : 'Walk-forward analysis failed.');
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const p = buildParams();
      const qs = new URLSearchParams({
        start_date: p.start_date,
        end_date: p.end_date,
        starting_capital: String(p.starting_capital),
      });
      if (p.symphony_id) qs.set('symphony_id', p.symphony_id);
      if (p.split_date) qs.set('split_date', p.split_date);
      if (p.custom_stress_periods && p.custom_stress_periods.length > 0) {
        qs.set('custom_stress_periods', JSON.stringify(p.custom_stress_periods));
      }
      if (wfRunId) qs.set('run_id', wfRunId);
      const resp = await fetch(`/api/backtest/walkforward/export.csv?${qs}`, {
        credentials: 'same-origin',
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `walkforward_${startDate}_${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // ignore — toast would go here
    } finally {
      setDownloading(false);
    }
  };

  const isDegraded = result?.error && (result.in_sample || result.out_of_sample || result.yearly_periods.length > 0);

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-indigo-600" />
          Robustness Tests
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-medium whitespace-nowrap">IS/OOS split</label>
            <input
              type="date"
              value={splitDate}
              onChange={(e) => setSplitDate(e.target.value)}
              className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 disabled:opacity-60"
              disabled={status === 'running'}
            />
            {splitDate && (
              <button onClick={() => setSplitDate('')} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
          {status === 'done' && result && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {downloading ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
          <button
            onClick={handleRun}
            disabled={status === 'running' || dateRangeTooShort}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {status === 'running'
              ? <><RefreshCw className="w-4 h-4 animate-spin" /> Running… {elapsed}s</>
              : <><Play className="w-4 h-4" /> Run Tests</>}
          </button>
        </div>
      </div>

      {/* Custom stress period builder */}
      <div className="px-6 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom Stress Periods</span>
          {customStress.map((p, i) => (
            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-rose-100 text-rose-700 font-medium">
              {p.label}
              <button onClick={() => handleRemoveStress(i)} className="text-rose-400 hover:text-rose-700 ml-0.5">✕</button>
            </span>
          ))}
          {!showStressForm && (
            <button
              onClick={() => setShowStressForm(true)}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline"
            >
              + Add period
            </button>
          )}
        </div>
        {showStressForm && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <input
              type="text"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="Label (optional)"
              className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg w-36 focus:ring-1 focus:ring-indigo-400"
            />
            <input
              type="date"
              value={newStart}
              onChange={e => setNewStart(e.target.value)}
              className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400"
            />
            <span className="text-xs text-gray-400">→</span>
            <input
              type="date"
              value={newEnd}
              onChange={e => setNewEnd(e.target.value)}
              className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={handleAddStress}
              disabled={!newStart || !newEnd}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Add
            </button>
            <button
              onClick={() => { setShowStressForm(false); setNewLabel(''); setNewStart(''); setNewEnd(''); }}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Idle */}
      {status === 'idle' && (
        dateRangeTooShort ? (
          <div className="px-6 py-6">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-800 text-sm font-semibold">Date range too short for robustness analysis</p>
                <p className="text-amber-700 text-xs mt-1">
                  Walk-forward analysis requires at least 30 days. Your current backtest covers{' '}
                  <span className="font-semibold">{dateRangeDays} day{dateRangeDays !== 1 ? 's' : ''}</span>{' '}
                  ({startDate} → {endDate}).
                </p>
                <p className="text-amber-600 text-xs mt-1">
                  Extend the <span className="font-semibold">start date</span> in the backtest form above, then re-run the backtest.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-gray-400 text-sm">
            Split the backtest into{' '}
            <span className="font-semibold text-gray-600">in-sample + out-of-sample</span>
            {' '}periods, get per-year results, and stress-test against market drawdowns.
            <div className="mt-2 text-xs text-gray-400">Default split: 80% in-sample / 20% out-of-sample by calendar date</div>
          </div>
        )
      )}

      {/* Running */}
      {status === 'running' && (
        <div className="px-6 py-10 flex flex-col items-center gap-3 text-gray-500">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
          <p className="text-sm">Running sub-period backtests — uses cached results when available…</p>
          <p className="text-xs text-gray-400">{elapsed}s elapsed</p>
        </div>
      )}

      {/* Error (non-partial) */}
      {status === 'error' && (
        <div className="px-6 py-4">
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-red-800 text-sm">{wfError}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {status === 'done' && result && (
        <div className="px-6 pb-6 space-y-6 pt-4">

          {/* Partial warning */}
          {isDegraded && (
            <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-amber-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{result.error}</span>
            </div>
          )}

          {/* IS vs OOS comparison */}
          {(result.in_sample || result.out_of_sample) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide flex items-center gap-2">
                <span>In-Sample vs Out-of-Sample</span>
                <span className="text-xs font-normal text-gray-400 normal-case">
                  split {result.split_date} · {result.split_method === 'auto_80_20' ? 'auto 80/20' : 'custom'}
                </span>
              </h3>
              <div className="flex flex-wrap gap-4">
                {result.in_sample && <PeriodMetricCard period={result.in_sample} label="In-Sample" />}
                {result.out_of_sample && <PeriodMetricCard period={result.out_of_sample} label="Out-of-Sample" />}
              </div>
              {result.in_sample && result.out_of_sample && (() => {
                const drift = result.out_of_sample.annualized_return_pct - result.in_sample.annualized_return_pct;
                const driftColor = Math.abs(drift) < 3 ? 'text-emerald-700' : Math.abs(drift) < 8 ? 'text-amber-600' : 'text-red-600';
                const verdict = Math.abs(drift) < 3 ? 'Consistent' : Math.abs(drift) < 8 ? 'Moderate drift' : 'Significant drift';
                return (
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <span>CAGR drift (OOS − IS):</span>
                    <span className={`font-semibold ${driftColor}`}>{fmtPct(drift)}</span>
                    <span className={`font-medium ${driftColor}`}>— {verdict}</span>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Yearly periods table */}
          {result.yearly_periods.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Year-by-Year Breakdown</h3>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Year</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Return</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">CAGR</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Max DD</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Sharpe</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Worst Day</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Win Rate</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Trades</th>
                      <th className="text-right py-2.5 px-3 font-semibold text-gray-500 whitespace-nowrap">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.yearly_periods.map((p) => (
                      <tr key={p.label} className="hover:bg-gray-50">
                        <td className="py-2 px-3 font-semibold text-gray-800">{p.label}</td>
                        <td className={`py-2 px-3 text-right font-semibold ${p.total_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {fmtPctBare(p.total_return_pct)}
                        </td>
                        <td className={`py-2 px-3 text-right ${p.annualized_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {fmtPctBare(p.annualized_return_pct)}
                        </td>
                        <td className="py-2 px-3 text-right text-red-600">-{p.max_drawdown_pct.toFixed(1)}%</td>
                        <td className={`py-2 px-3 text-right ${p.sharpe_ratio >= 1 ? 'text-emerald-700' : p.sharpe_ratio >= 0 ? 'text-gray-600' : 'text-red-600'}`}>
                          {p.sharpe_ratio.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right text-red-600">{p.worst_day_pct.toFixed(2)}%</td>
                        <td className="py-2 px-3 text-right text-gray-500">{p.win_rate_pct.toFixed(1)}%</td>
                        <td className="py-2 px-3 text-right text-gray-600">{p.total_trades.toLocaleString()}</td>
                        <td className="py-2 px-3 text-right text-gray-400">{p.total_trading_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Stress periods */}
          {result.stress_periods.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Stress Periods</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {result.stress_periods.map((p) => (
                  <div key={p.label} className="bg-rose-50/50 border border-rose-200 rounded-xl p-4">
                    <div className="text-xs font-bold text-rose-700 mb-1">{p.label}</div>
                    <div className="text-xs text-gray-400 mb-3">{p.start_date} → {p.end_date} · {p.total_trading_days}d · {p.total_trades} trades</div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Return</span>
                        <span className={`font-semibold ${p.total_return_pct >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtPctBare(p.total_return_pct)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Max DD</span>
                        <span className="text-red-600">-{p.max_drawdown_pct.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Sharpe</span>
                        <span className="text-gray-700">{p.sharpe_ratio.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Worst Day</span>
                        <span className="text-red-600">{p.worst_day_pct.toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No data at all */}
          {!result.in_sample && !result.out_of_sample && result.yearly_periods.length === 0 && result.stress_periods.length === 0 && (
            <div className="py-8 text-center text-gray-400 text-sm">
              No sub-period data available. The date range may be too short or data is unavailable.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
